//* Import react hooks
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Import API functions
import { attributionAPI } from "../../../hooks/demand/attributions/attributionAPI";

//? Import types from pagination
import { Pagination } from "../../../services/stores/types";

//? Import types
import {
  AttributionDetails,
  CreateAttributionInput,
  UpdateAttributionInput,
} from "../../../hooks/demand/attributions/types/types";

/**
 * Custom hook to manage attributions.
 *
 * @param {string} token - The authentication token.
 * @param {string} [demandId] - The optional demand ID to filter attributions.
 * @returns {object} - The hook's return values and functions.
 * @returns {Array} attributions - The list of attributions.
 * @returns {boolean} loading - Indicates if the data is loading.
 * @returns {string | null} error - The error message if an error occurred.
 * @returns {string} search - The current search term.
 * @returns {function} setSearch - Function to set the search term.
 * @returns {Pagination} pagination - The current pagination state.
 * @returns {function} setPagination - Function to set the pagination state.
 * @returns {function} refetch - Function to refetch the data.
 * @returns {function} createAttribution - Function to create a new attribution.
 * @returns {function} updateAttribution - Function to update an existing attribution.
 * @returns {function} deleteAttribution - Function to delete an attribution.
 * @returns {function} getAttributionById - Function to get attribution details by ID.
 * @returns {function} changePage - Function to change the current page.
 * @returns {boolean} isCreating - Indicates if an attribution is being created.
 * @returns {boolean} isUpdating - Indicates if an attribution is being updated.
 * @returns {boolean} isDeleting - Indicates if an attribution is being deleted.
 * @returns {string | null} createdAttributionId - The ID of the newly created attribution.
 */
export const useAttributions = (token: string, demandId?: string) => {
  const queryClient = useQueryClient();

  // State to manage search term
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // State to manage status filter
  const [status, setStatus] = useState<string>("");

  // Fetch attributions based on search term, status, and pagination
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  // Refetch attributions based on status
  const handleStatus = useCallback((newStatus: string) => {
    setStatus(newStatus);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  // Function to change the current page
  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // Including "status" in the query key to trigger refetch on changes
  const queryKey = [
    "attributions",
    {
      search,
      status,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
      demandId,
    },
  ] as const;

  const fetchFn = () => {
    if (demandId) {
      return attributionAPI.getByDemandId(
        demandId,
        token,
        search,
        status,
        pagination.currentPage,
        pagination.itemsPerPage
      );
    } else {
      return attributionAPI.getAll(
        search,
        pagination.currentPage,
        token,
        status,
        pagination.itemsPerPage
      );
    }
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: fetchFn,
    staleTime: 5 * 60 * 1000,
  });

  const attributions = data?.attributions || [];
  const fetchedPagination = data?.paginations || pagination;

  const createAttributionMutation = useMutation({
    mutationFn: (attributionData: CreateAttributionInput) =>
      attributionAPI.create(attributionData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributions"] });
    },
  });

  const updateAttributionMutation = useMutation({
    mutationFn: ({ id, ...attributionData }: UpdateAttributionInput) =>
      attributionAPI.update(id, { ...attributionData }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributions"] });
    },
  });

  const deleteAttributionMutation = useMutation({
    mutationFn: (id: string) => attributionAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributions"] });
    },
  });

  const updateAttributionStatusMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      attributionAPI.updateStatus(id, type, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributions"] });
    },
  });

  const getAttributionById = useCallback(
    async (id: string): Promise<AttributionDetails | null> => {
      try {
        const attributionDetails = await attributionAPI.getById(id, token);
        return attributionDetails;
      } catch (err) {
        console.error("Error fetching Attribution by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    attributions,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    status,
    setStatus: handleStatus,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createAttribution: createAttributionMutation.mutateAsync,
    updateAttribution: updateAttributionMutation.mutateAsync,
    deleteAttribution: deleteAttributionMutation.mutateAsync,
    updateStatusAttribution: updateAttributionStatusMutation.mutateAsync,
    getAttributionById,
    changePage,
    isCreating: createAttributionMutation.isPending,
    isUpdating: updateAttributionMutation.isPending,
    isDeleting: deleteAttributionMutation.isPending,
    isUpdatingStatus: updateAttributionStatusMutation.isPending,
  };
};
